"use client"

import { useEffect, useState, useMemo } from "react"
import { useTranslations } from "next-intl"
import { IconLoader2 } from "@tabler/icons-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { getTransferAgents } from "@/lib/telephony-api"
import type { TransferAgent } from "@/types"

interface TransferDialogProps {
  open: boolean
  onClose: () => void
  onBlindTransfer: (extension: string, name: string) => void
  onAttendedTransfer: (extension: string, name: string) => void
  currentExtension: string
}

export function TransferDialog({
  open,
  onClose,
  onBlindTransfer,
  onAttendedTransfer,
  currentExtension,
}: TransferDialogProps) {
  const t = useTranslations("telephony")
  const [agents, setAgents] = useState<TransferAgent[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")

  useEffect(() => {
    if (!open) {
      setSearch("")
      return
    }

    let cancelled = false
    setLoading(true)
    getTransferAgents()
      .then((data) => {
        if (!cancelled) setAgents(data)
      })
      .catch(() => {
        if (!cancelled) setAgents([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open])

  const filteredAgents = useMemo(() => {
    const available = agents.filter(
      (a) => a.sip_extension !== currentExtension
    )
    if (!search.trim()) return available

    const q = search.toLowerCase()
    return available.filter(
      (a) =>
        a.full_name.toLowerCase().includes(q) ||
        a.first_name.toLowerCase().includes(q) ||
        a.last_name.toLowerCase().includes(q) ||
        a.sip_extension.includes(q)
    )
  }, [agents, search, currentExtension])

  const handleBlind = (agent: TransferAgent) => {
    onBlindTransfer(agent.sip_extension, agent.full_name)
  }

  const handleAttended = (agent: TransferAgent) => {
    onAttendedTransfer(agent.sip_extension, agent.full_name)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[340px] p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base">{t("transferDialog.title")}</DialogTitle>
          <DialogDescription className="text-xs">
            {t("transferDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 pb-2">
          <Input
            placeholder={t("transferDialog.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>

        <ScrollArea className="max-h-[280px] px-2 pb-3">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && filteredAgents.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("transferDialog.noAgents")}
            </p>
          )}

          {!loading &&
            filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-accent"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm truncate">{agent.full_name}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                    {agent.sip_extension}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => handleBlind(agent)}
                  >
                    {t("transferDialog.transfer")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => handleAttended(agent)}
                  >
                    {t("transferDialog.consult")}
                  </Button>
                </div>
              </div>
            ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
